import { LOCALES, DEFAULT_LANG, langPrefix, LOCALE_CODES } from "@/i18n/locales";
import { EGGCALC_SLUGS, eggcalcPath } from "@/i18n/eggcalc";
import { HENPROFIT_SLUGS, henprofitPath } from "@/i18n/henprofit";
import { HATCH_SLUGS, hatchPath } from "@/i18n/hatch";
import { FEED_SLUGS, feedPath } from "@/i18n/feed";
import { COOP_SLUGS, coopPath } from "@/i18n/coop";
import { USECASE_KEYS, USECASE_SLUGS, usecasePath } from "@/i18n/usecases";
import { SPECIES_KEYS, SPECIES_SLUGS, speciesPath } from "@/i18n/breeding";

// Localized marketing pages (features / pricing / faq): slugs + SEO meta per language.
// Auto-generated (natural, market-specific — not literal translations).
export const PAGE_KEYS = ["features", "pricing", "faq"];

export const PAGE_SLUGS = {
  "features": {
    "fr": "fonctionnalites",
    "en": "features",
    "es": "funcionalidades",
    "it": "funzionalita",
    "de": "funktionen",
    "nl": "functies",
    "sv": "funktioner",
    "da": "funktioner",
    "bg": "funktsii",
    "el": "dynatotites",
    "hr": "znacajke",
    "et": "funktsioonid",
    "fi": "ominaisuudet",
    "hu": "funkciok",
    "lv": "funkcijas",
    "lt": "funkcijos",
    "pl": "funkcje",
    "pt": "funcionalidades",
    "ro": "functionalitati",
    "sk": "funkcie",
    "sl": "funkcije",
    "cs": "funkce"
  },
  "pricing": {
    "fr": "tarifs",
    "en": "pricing",
    "es": "precios",
    "it": "prezzi",
    "de": "preise",
    "nl": "prijzen",
    "sv": "priser",
    "da": "priser",
    "bg": "tseni",
    "el": "times",
    "hr": "cijene",
    "et": "hinnad",
    "fi": "hinnat",
    "hu": "arak",
    "lv": "cenas",
    "lt": "kainos",
    "pl": "cennik",
    "pt": "precos",
    "ro": "preturi",
    "sk": "cennik",
    "sl": "cenik",
    "cs": "cenik"
  },
  "faq": {
    "fr": "faq",
    "en": "faq",
    "es": "faq",
    "it": "faq",
    "de": "faq",
    "nl": "faq",
    "sv": "faq",
    "da": "faq",
    "bg": "faq",
    "el": "faq",
    "hr": "faq",
    "et": "faq",
    "fi": "faq",
    "hu": "faq",
    "lv": "faq",
    "lt": "faq",
    "pl": "faq",
    "pt": "faq",
    "ro": "faq",
    "sk": "faq",
    "sl": "faq",
    "cs": "faq"
  }
};

export const PAGE_META = {
  "features": {
    "fr": {
      "title": "Fonctionnalités – Ma Petite Ferme",
      "description": "Gérez votre élevage, vos cultures et votre boutique en ligne depuis un seul outil. Découvrez tout ce que Ma Petite Ferme peut faire pour votre exploitation.",
      "h1": "Tout pour gérer votre ferme"
    },
    "en": {
      "title": "Features – Ma Petite Ferme",
      "description": "From livestock tracking to crop planning and your own farm shop — Ma Petite Ferme puts every tool you need in one place. See what's possible for your farm.",
      "h1": "Everything your farm needs"
    },
    "es": {
      "title": "Funcionalidades – Ma Petite Ferme",
      "description": "Gestiona tu ganadería, tus cultivos y tu tienda agrícola desde una sola plataforma. Descubre cómo Ma Petite Ferme transforma la gestión de tu explotación.",
      "h1": "Todo para tu explotación agrícola"
    },
    "it": {
      "title": "Funzionalità – Ma Petite Ferme",
      "description": "Gestisci allevamento, coltivazioni e il tuo spaccio aziendale in un'unica piattaforma. Scopri come Ma Petite Ferme semplifica ogni aspetto della tua azienda agricola.",
      "h1": "Tutto per la tua azienda agricola"
    },
    "de": {
      "title": "Funktionen – Ma Petite Ferme",
      "description": "Tierhaltung, Ackerbau und Hofladen-Verwaltung in einer einzigen Lösung. Entdecken Sie, wie Ma Petite Ferme Ihren landwirtschaftlichen Betrieb vereinfacht.",
      "h1": "Alle Werkzeuge für Ihren Hof"
    },
    "nl": {
      "title": "Functies – Ma Petite Ferme",
      "description": "Beheer uw veestapel, gewassen en boerderijwinkel vanuit één platform. Ontdek hoe Ma Petite Ferme het dagelijks werk op uw bedrijf eenvoudiger maakt.",
      "h1": "Alles voor uw boerderij"
    },
    "sv": {
      "title": "Funktioner – Ma Petite Ferme",
      "description": "Hantera djurhållning, grödor och din gårdsbutik på ett och samma ställe. Se hur Ma Petite Ferme gör vardagen enklare för ditt lantbruk.",
      "h1": "Allt för ditt lantbruk"
    },
    "da": {
      "title": "Funktioner – Ma Petite Ferme",
      "description": "Styr husdyrhold, afgrøder og din gårdsbutik fra ét samlet system. Oplev hvordan Ma Petite Ferme gør din landbrugsdrift nemmere og mere overskuelig.",
      "h1": "Alt til din landbrugsdrift"
    },
    "bg": {
      "title": "Функции – Ma Petite Ferme",
      "description": "Управлявайте животновъдството, растениевъдството и онлайн магазина си от една платформа. Вижте как Ma Petite Ferme улеснява вашето стопанство.",
      "h1": "Всичко за вашето стопанство"
    },
    "el": {
      "title": "Δυνατότητες – Ma Petite Ferme",
      "description": "Διαχειριστείτε κτηνοτροφία, καλλιέργειες και το ηλεκτρονικό σας αγροκτηνοτροφικό κατάστημα σε μία πλατφόρμα. Δείτε τι προσφέρει το Ma Petite Ferme.",
      "h1": "Όλα για τη φάρμα σας"
    },
    "hr": {
      "title": "Značajke – Ma Petite Ferme",
      "description": "Upravljajte stokom, usjevima i svojom poljoprivrednom trgovinom s jedne platforme. Otkrijte kako Ma Petite Ferme olakšava svakodnevno vođenje gospodarstva.",
      "h1": "Sve za vaše gospodarstvo"
    },
    "et": {
      "title": "Funktsioonid – Ma Petite Ferme",
      "description": "Halda loomakasvatust, põllukultuure ja talupoe müüki ühest platvormist. Vaata, kuidas Ma Petite Ferme teeb sinu talu juhtimise lihtsamaks.",
      "h1": "Kõik teie talu jaoks"
    },
    "fi": {
      "title": "Ominaisuudet – Ma Petite Ferme",
      "description": "Hallinnoi karjataloutta, viljelyä ja tilasi verkkokauppaa yhdessä järjestelmässä. Tutustu siihen, miten Ma Petite Ferme helpottaa maatilasi arkea.",
      "h1": "Kaikki maatilasi tarpeisiin"
    },
    "hu": {
      "title": "Funkciók – Ma Petite Ferme",
      "description": "Kezelje állattartását, növénytermesztését és gazdasági webáruházát egyetlen platformon. Fedezze fel, hogyan egyszerűsíti a Ma Petite Ferme a gazdaság vezetését.",
      "h1": "Minden a gazdaságod számára"
    },
    "lv": {
      "title": "Funkcijas – Ma Petite Ferme",
      "description": "Pārvaldiet lopkopību, augkopību un savas saimniecības interneta veikalu vienā platformā. Uzziniet, kā Ma Petite Ferme atvieglo ikdienas darbu saimniecībā.",
      "h1": "Viss jūsu saimniecībai"
    },
    "lt": {
      "title": "Funkcijos – Ma Petite Ferme",
      "description": "Valdykite gyvulininkystę, augalininkystę ir ūkio internetinę parduotuvę vienoje platformoje. Sužinokite, kaip Ma Petite Ferme palengvina ūkio valdymą.",
      "h1": "Viskas jūsų ūkiui"
    },
    "pl": {
      "title": "Funkcje – Ma Petite Ferme",
      "description": "Zarządzaj hodowlą, uprawami i sklepem internetowym gospodarstwa z jednej platformy. Sprawdź, jak Ma Petite Ferme ułatwia codzienne prowadzenie gospodarstwa rolnego.",
      "h1": "Wszystko dla Twojego gospodarstwa"
    },
    "pt": {
      "title": "Funcionalidades – Ma Petite Ferme",
      "description": "Gira a criação de animais, as culturas e a sua loja agrícola online numa só plataforma. Descubra como o Ma Petite Ferme simplifica a gestão da sua exploração.",
      "h1": "Tudo para a sua exploração"
    },
    "ro": {
      "title": "Funcționalități – Ma Petite Ferme",
      "description": "Gestionați creșterea animalelor, culturile și magazinul online al fermei dintr-o singură platformă. Descoperiți cum Ma Petite Ferme simplifică administrarea exploatației.",
      "h1": "Tot pentru ferma dumneavoastră"
    },
    "sk": {
      "title": "Funkcie – Ma Petite Ferme",
      "description": "Spravujte chov zvierat, rastlinnú výrobu a farmársky e-shop z jednej platformy. Zistite, ako Ma Petite Ferme zjednodušuje každodenné riadenie vášho hospodárstva.",
      "h1": "Všetko pre vaše hospodárstvo"
    },
    "sl": {
      "title": "Funkcije – Ma Petite Ferme",
      "description": "Upravljajte živinorejo, poljedelstvo in spletno kmetijsko trgovino z ene platforme. Odkrijte, kako Ma Petite Ferme poenostavlja vodenje vašega kmetijskega gospodarstva.",
      "h1": "Vse za vaše kmetijstvo"
    },
    "cs": {
      "title": "Funkce – Ma Petite Ferme",
      "description": "Spravujte chov zvířat, rostlinnou výrobu a farmářský e-shop z jediné platformy. Zjistěte, jak Ma Petite Ferme zjednodušuje každodenní řízení vašeho hospodářství.",
      "h1": "Vše pro vaše hospodářství"
    }
  },
  "pricing": {
    "fr": {
      "title": "Tarifs – Ma Petite Ferme",
      "description": "Des formules adaptées à chaque exploitation, du maraîcher indépendant à la grande ferme. Trouvez le plan Ma Petite Ferme qui correspond à votre activité et votre budget.",
      "h1": "Des tarifs pour chaque ferme"
    },
    "en": {
      "title": "Pricing – Ma Petite Ferme",
      "description": "Flexible plans for smallholders, family farms and large agricultural operations. Find the Ma Petite Ferme plan that fits your farm size and budget — no hidden fees.",
      "h1": "Plans for every farm size"
    },
    "es": {
      "title": "Precios – Ma Petite Ferme",
      "description": "Planes flexibles para pequeños agricultores, ganaderos y grandes explotaciones. Elige el plan de Ma Petite Ferme que se adapta a tu negocio sin costes ocultos.",
      "h1": "Precios para cada explotación"
    },
    "it": {
      "title": "Prezzi – Ma Petite Ferme",
      "description": "Piani flessibili per piccoli agricoltori, allevatori e grandi aziende agricole. Scegli l'offerta Ma Petite Ferme più adatta alle tue esigenze senza costi nascosti.",
      "h1": "Piani per ogni azienda agricola"
    },
    "de": {
      "title": "Preise – Ma Petite Ferme",
      "description": "Flexible Tarife für Kleinbetriebe, Familienhöfe und große Landwirtschaftsbetriebe. Finden Sie den Ma Petite Ferme-Plan, der zu Ihrem Hof passt – ohne versteckte Kosten.",
      "h1": "Tarife für jeden Betrieb"
    },
    "nl": {
      "title": "Prijzen – Ma Petite Ferme",
      "description": "Flexibele abonnementen voor kleine tuinders, gezinsbedrijven en grote agrarische ondernemingen. Kies het Ma Petite Ferme-plan dat past bij uw bedrijf en budget.",
      "h1": "Prijzen voor elk bedrijf"
    },
    "sv": {
      "title": "Priser – Ma Petite Ferme",
      "description": "Flexibla abonnemang för småbrukare, familjejordbruk och större lantbruksföretag. Hitta det Ma Petite Ferme-paket som passar ditt lantbruk och din budget.",
      "h1": "Priser för varje lantbruk"
    },
    "da": {
      "title": "Priser – Ma Petite Ferme",
      "description": "Fleksible abonnementer til hobbylandbrug, familiebrug og større landbrugsbedrifter. Find den Ma Petite Ferme-plan der passer til din drift og dit budget.",
      "h1": "Priser til enhver drift"
    },
    "bg": {
      "title": "Цени – Ma Petite Ferme",
      "description": "Гъвкави планове за малки ферми, семейни стопанства и големи земеделски предприятия. Намерете плана на Ma Petite Ferme, подходящ за вашето стопанство и бюджет.",
      "h1": "Цени за всяко стопанство"
    },
    "el": {
      "title": "Τιμές – Ma Petite Ferme",
      "description": "Ευέλικτα πακέτα για μικρές εκμεταλλεύσεις, οικογενειακές αγροτικές επιχειρήσεις και μεγάλες φάρμες. Βρείτε το πλάνο Ma Petite Ferme που ταιριάζει στις ανάγκες σας.",
      "h1": "Τιμές για κάθε εκμετάλλευση"
    },
    "hr": {
      "title": "Cijene – Ma Petite Ferme",
      "description": "Fleksibilni planovi za mala obiteljska gospodarstva i velika poljoprivredna poduzeća. Pronađite Ma Petite Ferme paket koji odgovara vašim potrebama i proračunu.",
      "h1": "Cijene za svako gospodarstvo"
    },
    "et": {
      "title": "Hinnad – Ma Petite Ferme",
      "description": "Paindlikud paketid väiketaludele, perefarmidele ja suurematele põllumajandusettevõtetele. Leia Ma Petite Ferme plaan, mis sobib sinu talu suuruse ja eelarvega.",
      "h1": "Hinnad igale talule"
    },
    "fi": {
      "title": "Hinnat – Ma Petite Ferme",
      "description": "Joustavat paketit pientiloille, perheviljelmille ja suuremmille maatalousyrityksille. Löydä Ma Petite Ferme -suunnitelma, joka sopii tilasi tarpeisiin ja budjettiin.",
      "h1": "Hinnat jokaiselle maatilalle"
    },
    "hu": {
      "title": "Árak – Ma Petite Ferme",
      "description": "Rugalmas csomagok kisgazdaságok, családi farmok és nagyobb mezőgazdasági vállalkozások számára. Találja meg a Ma Petite Ferme-csomagot, amely illik a gazdaságához.",
      "h1": "Árak minden gazdaságnak"
    },
    "lv": {
      "title": "Cenas – Ma Petite Ferme",
      "description": "Elastīgi plāni mazajām saimniecībām, ģimenes farmām un lielākām lauksaimniecības uzņēmumiem. Atrodiet Ma Petite Ferme plānu, kas atbilst jūsu vajadzībām un budžetam.",
      "h1": "Cenas katrai saimniecībai"
    },
    "lt": {
      "title": "Kainos – Ma Petite Ferme",
      "description": "Lankstūs planai mažiems ūkiams, šeimos fermoms ir didesnėms žemės ūkio įmonėms. Raskite Ma Petite Ferme planą, atitinkantį jūsų ūkio poreikius ir biudžetą.",
      "h1": "Kainos kiekvienam ūkiui"
    },
    "pl": {
      "title": "Cennik – Ma Petite Ferme",
      "description": "Elastyczne plany dla małych gospodarstw, rodzinnych farm i dużych przedsiębiorstw rolnych. Znajdź pakiet Ma Petite Ferme dopasowany do wielkości i budżetu Twojego gospodarstwa.",
      "h1": "Cennik dla każdego gospodarstwa"
    },
    "pt": {
      "title": "Preços – Ma Petite Ferme",
      "description": "Planos flexíveis para pequenos agricultores, explorações familiares e grandes empresas agrícolas. Encontre o plano Ma Petite Ferme adequado ao seu negócio e orçamento.",
      "h1": "Planos para cada exploração"
    },
    "ro": {
      "title": "Prețuri – Ma Petite Ferme",
      "description": "Planuri flexibile pentru fermieri individuali, ferme de familie și mari exploatații agricole. Găsiți pachetul Ma Petite Ferme potrivit pentru afacerea și bugetul dumneavoastră.",
      "h1": "Prețuri pentru orice fermă"
    },
    "sk": {
      "title": "Cenník – Ma Petite Ferme",
      "description": "Flexibilné plány pre malé hospodárstva, rodinné farmy aj veľké poľnohospodárske podniky. Nájdite balík Ma Petite Ferme, ktorý zodpovedá vašim potrebám a rozpočtu.",
      "h1": "Cenník pre každé hospodárstvo"
    },
    "sl": {
      "title": "Cenik – Ma Petite Ferme",
      "description": "Prilagodljivi paketi za mala kmetijska gospodarstva, družinske farme in večja kmetijska podjetja. Poiščite Ma Petite Ferme paket, ki ustreza vašim potrebam in proračunu.",
      "h1": "Cenik za vsako kmetijo"
    },
    "cs": {
      "title": "Ceník – Ma Petite Ferme",
      "description": "Flexibilní plány pro malá hospodářství, rodinné farmy i velké zemědělské podniky. Najděte balíček Ma Petite Ferme, který odpovídá potřebám a rozpočtu vašeho hospodářství.",
      "h1": "Ceník pro každé hospodářství"
    }
  },
  "faq": {
    "fr": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Vous avez des questions sur Ma Petite Ferme ? Retrouvez toutes les réponses sur la gestion de l'élevage, la boutique en ligne et nos formules d'abonnement.",
      "h1": "Vos questions, nos réponses"
    },
    "en": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Got questions about Ma Petite Ferme? Find clear answers about farm management features, the online shop, subscription plans and getting started with your free trial.",
      "h1": "Your questions answered"
    },
    "es": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "¿Tienes dudas sobre Ma Petite Ferme? Aquí encontrarás respuestas claras sobre gestión ganadera, la tienda online, planes de suscripción y cómo empezar tu prueba gratuita.",
      "h1": "Tus preguntas, nuestras respuestas"
    },
    "it": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Hai domande su Ma Petite Ferme? Trova risposte chiare sulla gestione agricola, lo spaccio online, i piani in abbonamento e come iniziare la tua prova gratuita.",
      "h1": "Le tue domande, le nostre risposte"
    },
    "de": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Haben Sie Fragen zu Ma Petite Ferme? Hier finden Sie klare Antworten zu Hofmanagement, dem Hofladen, Abonnements und wie Sie kostenlos starten können.",
      "h1": "Ihre Fragen, unsere Antworten"
    },
    "nl": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Vragen over Ma Petite Ferme? Hier vindt u duidelijke antwoorden over bedrijfsbeheer, de boerderijwinkel, abonnementen en hoe u gratis kunt beginnen.",
      "h1": "Uw vragen, onze antwoorden"
    },
    "sv": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Har du frågor om Ma Petite Ferme? Hitta tydliga svar om gårdshantering, gårdsbutiken, prenumerationsplaner och hur du kommer igång med din kostnadsfria provperiod.",
      "h1": "Dina frågor, våra svar"
    },
    "da": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Har du spørgsmål om Ma Petite Ferme? Find klare svar om landbrugsstyring, gårdsbutikken, abonnementer og hvordan du kommer gratis i gang med din prøveperiode.",
      "h1": "Dine spørgsmål, vores svar"
    },
    "bg": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Имате въпроси за Ma Petite Ferme? Намерете ясни отговори за управлението на стопанството, онлайн магазина, абонаментните планове и безплатния пробен период.",
      "h1": "Вашите въпроси, нашите отговори"
    },
    "el": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Έχετε απορίες για το Ma Petite Ferme; Βρείτε ξεκάθαρες απαντήσεις για τη διαχείριση φάρμας, το ηλεκτρονικό κατάστημα, τα πακέτα συνδρομής και τη δωρεάν δοκιμή.",
      "h1": "Οι ερωτήσεις, οι απαντήσεις μας"
    },
    "hr": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Imate pitanja o Ma Petite Ferme? Pronađite jasne odgovore o upravljanju gospodarstvom, online trgovini, pretplatnim paketima i kako besplatno započeti korištenje.",
      "h1": "Vaša pitanja, naši odgovori"
    },
    "et": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Kas teil on küsimusi Ma Petite Ferme kohta? Leidke selged vastused talu haldamise, veebipoe, tellimusplaanide ja tasuta prooviperioodi alustamise kohta.",
      "h1": "Teie küsimused, meie vastused"
    },
    "fi": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Onko sinulla kysymyksiä Ma Petite Fermestä? Löydä selkeät vastaukset tilan hallinnasta, verkkokaupasta, tilaussuunnitelmista ja ilmaisen kokeilujakson aloittamisesta.",
      "h1": "Kysymyksesi, vastauksemme"
    },
    "hu": {
      "title": "GYIK – Ma Petite Ferme",
      "description": "Kérdései vannak a Ma Petite Ferme-mel kapcsolatban? Találjon egyértelmű válaszokat a gazdaságirányításról, a webáruházról, az előfizetési csomagokról és az ingyenes próbáról.",
      "h1": "Kérdéseik, válaszaink"
    },
    "lv": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Vai jums ir jautājumi par Ma Petite Ferme? Atrodiet skaidras atbildes par saimniecības pārvaldību, interneta veikalu, abonēšanas plāniem un bezmaksas izmēģinājuma uzsākšanu.",
      "h1": "Jūsu jautājumi, mūsu atbildes"
    },
    "lt": {
      "title": "DUK – Ma Petite Ferme",
      "description": "Turite klausimų apie Ma Petite Ferme? Raskite aiškius atsakymus apie ūkio valdymą, internetinę parduotuvę, prenumeratos planus ir nemokamo bandomojo laikotarpio pradžią.",
      "h1": "Jūsų klausimai, mūsų atsakymai"
    },
    "pl": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Masz pytania dotyczące Ma Petite Ferme? Znajdź jasne odpowiedzi na temat zarządzania gospodarstwem, sklepu internetowego, planów subskrypcji i darmowego okresu próbnego.",
      "h1": "Wasze pytania, nasze odpowiedzi"
    },
    "pt": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Tem dúvidas sobre o Ma Petite Ferme? Encontre respostas claras sobre gestão agrícola, a loja online, planos de subscrição e como começar o seu período de teste gratuito.",
      "h1": "As suas dúvidas, as nossas respostas"
    },
    "ro": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Aveți întrebări despre Ma Petite Ferme? Găsiți răspunsuri clare despre gestionarea fermei, magazinul online, planurile de abonament și cum să începeți perioada de probă gratuită.",
      "h1": "Întrebările voastre, răspunsurile noastre"
    },
    "sk": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Máte otázky o Ma Petite Ferme? Nájdite jasné odpovede o riadení hospodárstva, e-shope, predplatných plánoch a o tom, ako začať s bezplatným skúšobným obdobím.",
      "h1": "Vaše otázky, naše odpovede"
    },
    "sl": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Imate vprašanja o Ma Petite Ferme? Poiščite jasne odgovore o upravljanju kmetije, spletni trgovini, naročniških paketih in kako začeti brezplačno preizkusno obdobje.",
      "h1": "Vaša vprašanja, naši odgovori"
    },
    "cs": {
      "title": "FAQ – Ma Petite Ferme",
      "description": "Máte otázky o Ma Petite Ferme? Najděte jasné odpovědi o řízení hospodářství, farmářském e-shopu, předplatných plánech a o tom, jak začít s bezplatným zkušebním obdobím.",
      "h1": "Vaše otázky, naše odpovědi"
    }
  }
};

export const FR_MARKETING_SLUGS = PAGE_KEYS.map((k) => PAGE_SLUGS[k].fr);

export function pagePath(key, lang) {
  const map = PAGE_SLUGS[key] || {};
  const slug = map[lang] || map[DEFAULT_LANG];
  return `${langPrefix(lang)}/${slug}`;
}

export function pageMeta(key, lang) {
  const m = PAGE_META[key] || {};
  return m[lang] || m[DEFAULT_LANG] || {};
}

export function buildPageAlternates(key, origin) {
  const o = (origin || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
  const alts = LOCALES.map((l) => ({ hreflang: l.hreflang, href: `${o}${pagePath(key, l.code)}` }));
  alts.push({ hreflang: "x-default", href: `${o}${pagePath(key, DEFAULT_LANG)}` });
  return alts;
}

// Public pages sharing the same slug across languages.
const PUBLIC_SAME_SLUG = ["blog"];
const LEGAL_SLUGS = ["cgu", "confidentialite", "cookies", "contact", "demo"];

// Equivalent URL of the CURRENT public route in another language (null if not a public route).
export function equivalentPath(pathname, targetLang) {
  const parts = (pathname || "/").split("/").filter(Boolean);
  let idx = 0;
  let curLang = DEFAULT_LANG;
  if (parts[0] && LOCALE_CODES.includes(parts[0]) && parts[0] !== DEFAULT_LANG) { curLang = parts[0]; idx = 1; }
  const rest = parts.slice(idx);
  const pfx = langPrefix(targetLang);
  if (rest.length === 0) return pfx || "/";                       // home
  if (rest[0] === "blog") return `${pfx}/${rest.join("/")}`;      // blog index or post (slug shared)
  if (rest[0] === "forum") return `${pfx}/${rest.join("/")}`;     // forum (category/slug keys shared across langs)
  if (rest.length === 1 && LEGAL_SLUGS.includes(rest[0])) return `/${rest[0]}`; // legal pages: single root route, language via context
  if (rest.length === 1 && PUBLIC_SAME_SLUG.includes(rest[0])) return `${pfx}/${rest[0]}`;
  if (rest.length === 1) {                                        // localized marketing page
    for (const key of PAGE_KEYS) {
      const slug = PAGE_SLUGS[key][curLang] || PAGE_SLUGS[key][DEFAULT_LANG];
      if (slug === rest[0]) return pagePath(key, targetLang);
    }
    // Localized SEO tools & landing pages (calculators, use-cases, breeding)
    if (EGGCALC_SLUGS[curLang] === rest[0]) return eggcalcPath(targetLang);
    if (HENPROFIT_SLUGS[curLang] === rest[0]) return henprofitPath(targetLang);
    if (HATCH_SLUGS[curLang] === rest[0]) return hatchPath(targetLang);
    if (FEED_SLUGS[curLang] === rest[0]) return feedPath(targetLang);
    if (COOP_SLUGS[curLang] === rest[0]) return coopPath(targetLang);
    for (const key of USECASE_KEYS) {
      if (USECASE_SLUGS[key] && USECASE_SLUGS[key][curLang] === rest[0]) return usecasePath(key, targetLang);
    }
    for (const key of SPECIES_KEYS) {
      if (SPECIES_SLUGS[key] && SPECIES_SLUGS[key][curLang] === rest[0]) return speciesPath(key, targetLang);
    }
  }
  return null;
}
